/**
 * @file edgeFunction.js
 * @description Zentraler Aufruf-Wrapper für Supabase Edge Functions.
 *
 * Was Edge Functions sind:
 * Edge Functions sind serverseitige JavaScript-/TypeScript-Programme, die sicher auf Servern
 * ausgeführt werden. Sie werden für Aufgaben genutzt, die sensible Geheimnisse (z. B. GitHub-API-Tokens,
 * Master-Keys) benötigen oder komplexe Rechteprüfungen durchführen, die nicht direkt im Browser
 * stattfinden dürfen.
 *
 * Dieser Wrapper übernimmt automatisch:
 * 1. Mitschicken des API-Keys und des angemeldeten Benutzer-Tokens (Bearer Token).
 * 2. Einheitliches Parsen von JSON-Antworten.
 * 3. Auslesen von HTTP-Statuscodes und eventuellen Rate-Limit-Wartezeiten (`Retry-After`).
 */

import { supabase } from './supabase';

const FUNCTIONS_BASE_URL = `${import.meta.env.VITE_SUPABASE_URL}/functions/v1`;

/**
 * Ruft eine Supabase Edge Function per HTTP POST auf.
 *
 * @param {string} functionName - Name der Edge Function (z. B. "admin-users", "auth-login")
 * @param {object} body - Die zu übertragenden Daten als JSON-Objekt
 * @param {boolean} [authenticated=true] - Steuert, ob das Session-Token des angemeldeten Benutzers
 *                                         mitgesendet werden soll (bei Login z. B. `false`)
 * @returns {Promise<any>} Die von der Edge Function zurückgegebene JSON-Antwort
 * @throws {Error} Wirft einen Fehler bei Netzwerkproblemen oder HTTP-Fehlercodes (>= 400)
 */
export async function callEdgeFunction(
  functionName,
  body,
  authenticated = true
) {
  const headers = {
    'Content-Type': 'application/json',
    'apikey': import.meta.env.VITE_SUPABASE_ANON_KEY,
  };

  // Falls authentifiziert: Token des aktuellen Benutzers anhängen
  if (authenticated) {
    const { data: { session } } = await supabase.auth.getSession();
    if (session?.access_token) {
      headers['Authorization'] = `Bearer ${session.access_token}`;
    }
  }

  const response = await fetch(`${FUNCTIONS_BASE_URL}/${functionName}`, {
    method: 'POST',
    headers,
    body: JSON.stringify(body),
  });

  // Antwort als JSON parsen; bei leeren oder HTML-Antworten sicher auf null zurückfallen
  let data = null;
  try {
    data = await response.json();
  } catch {
    data = null;
  }

  // Bei HTTP-Fehlercodes (z. B. 400 Bad Request, 403 Forbidden, 429 Too Many Requests) Fehler werfen
  if (!response.ok) {
    const error = new Error(data?.error ?? `Edge Function Fehler: ${response.status}`);
    // Statuscode anheften, damit Aufrufer z. B. 429 (Rate-Limit) gezielt behandeln können
    error.status = response.status;
    const retryAfter = response.headers.get('Retry-After');
    if (retryAfter) error.retryAfterSeconds = Number(retryAfter);
    throw error;
  }

  return data;
}
